
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Search, 
  FileText, 
  Briefcase,
  TrendingUp,
  FolderOpen,
  MessageSquare,
  Edit3,
  Eye
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useTemplatesByCategory } from '@/hooks/useTemplatesByCategory';
import { useUserCollections } from '@/hooks/useUserCollections';
import TemplateCanvas from './TemplateCanvas';

interface Template {
  id: string;
  title: string;
  description: string;
  content: string;
  category: string;
  type: 'saved' | 'bookmark' | 'popular' | 'product' | 'collection';
  custom_title?: string;
  notes?: string;
}

interface TemplatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: Template) => void;
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  isOpen,
  onClose,
  onSelectTemplate
}) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('popular');
  const [popularTemplates, setPopularTemplates] = useState<Template[]>([]);
  const [allTemplates, setAllTemplates] = useState<Template[]>([]);
  const [isLoadingPopular, setIsLoadingPopular] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [canvasTemplate, setCanvasTemplate] = useState<Template | null>(null);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  
  const { templates: categoryTemplates, isLoading: isLoadingCategories } = useTemplatesByCategory();
  const { collections, savedTemplates, isLoading: isLoadingCollections } = useUserCollections();

  // Load popular templates
  useEffect(() => {
    if (isOpen && selectedCategory === 'popular') {
      loadPopularTemplates();
    }
  }, [isOpen, selectedCategory]);

  // Load all templates for the "All" section
  useEffect(() => {
    if (isOpen && selectedCategory === 'all') {
      loadAllTemplates();
    }
  }, [isOpen, selectedCategory]);

  const loadPopularTemplates = async () => {
    setIsLoadingPopular(true);
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('id, title, description, content, likes, copies, categories(name)')
        .eq('status', 'approved')
        .order('copies', { ascending: false })
        .limit(50);

      if (error) throw error;

      const templates = data?.map(prompt => ({
        id: prompt.id,
        title: prompt.title,
        description: prompt.description,
        content: prompt.content,
        category: prompt.categories?.name || 'Popular',
        type: 'popular' as const
      })) || [];

      setPopularTemplates(templates);
    } catch (error) {
      console.error('Error loading popular templates:', error);
    } finally {
      setIsLoadingPopular(false);
    }
  };

  const loadAllTemplates = async () => {
    setIsLoadingAll(true);
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('id, title, description, content, categories(name)')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const templates = data?.map(prompt => ({
        id: prompt.id,
        title: prompt.title,
        description: prompt.description,
        content: prompt.content,
        category: prompt.categories?.name || 'General',
        type: 'product' as const
      })) || [];

      setAllTemplates(templates);
    } catch (error) {
      console.error('Error loading all templates:', error);
    } finally {
      setIsLoadingAll(false);
    }
  };

  const handleTemplateClick = (template: Template, action: 'use' | 'edit') => {
    console.log('Template clicked:', action, template.title);
    
    if (action === 'use') {
      console.log('Calling onSelectTemplate with:', template);
      onSelectTemplate(template);
      onClose(); // Close the template picker after selection
    } else {
      setCanvasTemplate(template);
      setIsCanvasOpen(true);
    }
  };

  const handleCanvasClose = () => {
    setIsCanvasOpen(false);
    setCanvasTemplate(null);
  };

  const handleCanvasCopyToChat = (content: string) => {
    if (canvasTemplate) {
      const updatedTemplate = {
        ...canvasTemplate,
        content: content
      };
      onSelectTemplate(updatedTemplate);
      setIsCanvasOpen(false);
      setCanvasTemplate(null);
      onClose();
    }
  };

  const filteredTemplates = (templates: Template[]) => {
    if (!searchQuery) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(template => 
      template.title.toLowerCase().includes(query) ||
      template.description.toLowerCase().includes(query)
    );
  };

  const renderTemplateCard = (template: Template) => (
    <Card
      key={template.id}
      className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-primary h-fit"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-sm line-clamp-2 flex items-center gap-2">
            <FileText className="h-3 w-3" />
            {template.custom_title || template.title}
          </CardTitle>
          <Badge variant="outline" className="text-xs flex-shrink-0">
            {template.category}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
          {template.description}
        </p>
        {template.notes && (
          <p className="text-xs text-blue-600 italic">
            Note: {template.notes}
          </p>
        )}
        <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t">
          <Button
            size="sm"
            variant="default"
            onClick={() => handleTemplateClick(template, 'use')}
            className="flex-1 h-8 text-xs"
          >
            <MessageSquare className="h-3 w-3 mr-1" />
            Use in Chat
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleTemplateClick(template, 'edit')}
            className="h-8 px-2"
          >
            <Edit3 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Group all templates by category for the "All" section
  const groupedAllTemplates = allTemplates.reduce((acc, template) => {
    const category = template.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<string, Template[]>);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Template Library
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Search Bar */}
            <div className="relative flex-shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Tabs */}
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="flex flex-col flex-1 min-h-0">
              <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
                <TabsTrigger value="popular" className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  Popular
                </TabsTrigger>
                <TabsTrigger value="product" className="flex items-center gap-1">
                  <Briefcase className="h-3 w-3" />
                  Product
                </TabsTrigger>
                <TabsTrigger value="collections" className="flex items-center gap-1">
                  <FolderOpen className="h-3 w-3" />
                  Collections
                </TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>

              {/* Popular Tab */}
              <TabsContent value="popular" className="flex-1 min-h-0 mt-4">
                <ScrollArea className="h-full">
                  {isLoadingPopular ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-1 pb-4">
                      {filteredTemplates(popularTemplates).map(renderTemplateCard)}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Product Tab */}
              <TabsContent value="product" className="flex-1 min-h-0 mt-4">
                <ScrollArea className="h-full">
                  {isLoadingCategories ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <Accordion type="multiple" className="w-full">
                      {Object.entries(categoryTemplates).map(([categoryName, templates]) => (
                        <AccordionItem key={categoryName} value={categoryName}>
                          <AccordionTrigger className="text-left">
                            <div className="flex items-center gap-2">
                              <Briefcase className="h-4 w-4" />
                              {categoryName}
                              <Badge variant="secondary" className="ml-2">
                                {templates.length}
                              </Badge>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                              {filteredTemplates(templates.map(t => ({
                                ...t,
                                type: 'product' as const
                              }))).map(renderTemplateCard)}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Collections Tab */}
              <TabsContent value="collections" className="flex-1 min-h-0 mt-4">
                <ScrollArea className="h-full">
                  {isLoadingCollections ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {collections.map(collection => (
                        <div key={collection.id}>
                          <h3 className="font-medium mb-3 flex items-center gap-2">
                            <FolderOpen className="h-4 w-4" />
                            {collection.name}
                            <Badge variant="secondary">{collection.templates.length}</Badge>
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                            {filteredTemplates(collection.templates.map(t => ({
                              ...t,
                              category: collection.name,
                              type: 'collection' as const
                            }))).map(renderTemplateCard)}
                          </div>
                        </div>
                      ))}

                      {savedTemplates.length > 0 && (
                        <div>
                          <h3 className="font-medium mb-3 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Saved Templates
                            <Badge variant="secondary">{savedTemplates.length}</Badge>
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filteredTemplates(savedTemplates.map(t => ({
                              ...t,
                              category: 'Saved',
                              type: 'saved' as const
                            }))).map(renderTemplateCard)}
                          </div>
                        </div>
                      )}

                      {collections.length === 0 && savedTemplates.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No collections or saved templates found</p>
                          <p className="text-sm">Save templates to see them here</p>
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              {/* All Tab - Show ALL templates grouped by category */}
              <TabsContent value="all" className="flex-1 min-h-0 mt-4">
                <ScrollArea className="h-full">
                  {isLoadingAll ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {Object.entries(groupedAllTemplates).map(([categoryName, templates]) => (
                        <div key={categoryName}>
                          <h3 className="font-medium mb-3 flex items-center gap-2">
                            <Briefcase className="h-4 w-4" />
                            {categoryName} ({templates.length})
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filteredTemplates(templates).map(renderTemplateCard)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Canvas Modal */}
      <TemplateCanvas
        isOpen={isCanvasOpen}
        onClose={handleCanvasClose}
        template={canvasTemplate}
        onCopyToChat={handleCanvasCopyToChat}
      />
    </>
  );
};

export default TemplatePicker;
